import { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useUserData } from "Flocknote/apiHooks";

import { useCardProvider } from "Payments/globalPaymentsStateHooks";

import {
  useACHIsEditing,
  useAccountHolderName,
  useExistingUserName,
  useName,
  useSubmitting,
} from "../paymentMethodStateHooks";

import { SmallNumberInput, iPhoneMini } from "./styledComponents";

const NameInput = () => {
  const ref = useRef();
  const { providerIsWePay } = useCardProvider();
  const { name, setName } = useName();
  const { existingUserName } = useExistingUserName();
  const [isEditing] = useACHIsEditing();
  const { submitting } = useSubmitting();

  // userHasTyped prevents the name from bring overwritten with a default value when the user deletes the whole name
  const [userHasTyped, setUserHasTyped] = useState(false);

  const { accountHolderName } = useAccountHolderName();
  const [userData] = useUserData();
  const userDataHasNameData =
    hasValue(userData?.fname) && hasValue(userData?.lname);
  const fullName = userDataHasNameData
    ? `${userData?.fname} ${userData?.lname}`
    : "";
  const defaultName = accountHolderName || fullName;

  const initializeName = useCallback(() => {
    if (userHasTyped) return;

    if (isEditing && !name?.length && existingUserName?.length) {
      setName(existingUserName);
    } else if (isEditing && !name?.length && defaultName?.length) {
      setName(defaultName);
    }
  }, [isEditing, name, setName, defaultName, existingUserName, userHasTyped]);

  const handleChange = (e) => {
    if (!userHasTyped) {
      setUserHasTyped(true);
    }
    setName(e.target.value);
  };

  useEffect(() => {
    initializeName();
  }, [initializeName]);

  return (
    <Wrapper data-cy="fund-it-ach-your-name" $isEditing={isEditing}>
      <Input
        value={isEditing || userHasTyped ? name : defaultName}
        ref={ref}
        type="text"
        name="ccname"
        autoComplete="cc-name"
        tabIndex={providerIsWePay ? 4 : 5}
        onChange={handleChange}
        placeholder="Account holder name"
        disabled={!isEditing || submitting}
      />
    </Wrapper>
  );
};

export default NameInput;

const Wrapper = styled.div`
  position: relative;
  max-width: fit-content;
  flex-grow: 0;
  padding: 4px 6px;
  margin-top: 0px;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: border 0.3s ease-in-out;
  box-shadow: ${({ $isEditing }) =>
    `0 0 0 1px rgba(255, 255, 255, ${$isEditing ? 0.175 : 0}) inset`};

  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    padding: 5px 10px 5px 10px;
  }

  &:hover {
    border: ${(p) =>
      p.$isEditing
        ? "2px solid rgba(255, 255, 255, 0.6)"
        : "2px solid transparent"};
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
    background: ${(p) =>
      p.$isEditing ? "rgba(255, 255, 255, 0.1)" : "transparent"};
  }
  &:focus-within {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
  }
`;

const Input = styled(SmallNumberInput)`
  font-size: 16px;
`;

function hasValue(value) {
  return value !== null && value !== undefined;
}
